import { LoadingButton } from "@mui/lab";
import { Box, Button, Chip, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { Link } from 'react-router-dom';
import { useEffect, useState } from "react";
import UploadIcon from '@mui/icons-material/Upload';
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import { Cancel, Visibility } from "@mui/icons-material";
import _ from "lodash";
import { FormProvider, RHFDateRangePickerField, RHFFileField, RHFSelectField, RHFTextField } from "../../../common/hook-form";
import { apiConstants, getGeneric, postGeneric, postMultipart } from "../../../common/constants";
import PageTitle from "../../../common/PageTitle";
import ProzoDataGrid from "../../../common/ProzoDataGrid";
import { parseDateAndTime, pickupRequestStatus } from "../../../helpers/UtilityHelper";
import Loader from "../../../common/Loader";
import MUIModal from "../../../common/MUIModal";
import DownloadSampleFile from "../../../common/DownloadSampleFile";
import { useGetPtlPurQuery } from "../../../redux/commonRTK";
import withLayout from "../../../hocs/Layout";
import { getMerchant } from "../../../helpers/ReduxHelpers";
import { WhitelistB2BCourier } from "../../../common/hook-form/CourierFilter";
import { useGetAllCourierPartnersQuery } from "../../../redux/commonRTK";
import ConfirmDialog from "../../../common/ConfirmDialog";
import { primaryTextSx, secondaryTextSx, awbTextSx, cellStackSx } from './cellStyles';
import { FONT,  WEIGHT } from '../../../styles/foundation/typography';
import { pageContainerSx, filterBarSx, primaryBtnSx, secondaryBtnSx, uploadSectionSx, btnRowSx } from '../../../styles/patterns';
import { SUCCESS, ERROR,  INFO,  SUBTLE } from '../../../styles/foundation/colors';


const PICKUP_STATUS_COLORS = {
    PICKUP_REQUESTED: INFO,
    OPEN: INFO,
    FAILED: ERROR,
    CANCELLED: SUBTLE,
    COURIER_ALLOCATED: SUCCESS,
    PICKUP_COMPLETED: SUCCESS,
    PICKUP_FAILED: ERROR,
};

//Terminal statuses — no actions (cancel/allocate) allowed once a PUR is closed.
const CLOSED_PICKUP_STATUSES = ['CANCELLED', 'PICKUP_COMPLETED', 'PICKUP_FAILED'];

const getPickupStatusChipSx = (status) => {
    const color = PICKUP_STATUS_COLORS[status] || SUBTLE;
    return {
        fontFamily: FONT,
        fontSize: 11,
        fontWeight: 600,
        height: 24,
        bgcolor: color + '18',
        color: color,
        border: '1px solid ' + color + '40',
        '& .MuiChip-label': { px: 1 },
    };
};

const getPaymentColorForPickup = (mode) => {
    return mode === 'COD' ? ERROR : SUCCESS;
};


function PtlPickupRequests() {
    const { enqueueSnackbar } = useSnackbar();
    const [sizePerPage, setSizePerPage] = useState(10);
    const [page, setPage] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [selectedReferences, setSelectedReferences] = useState([]);
    const [isCourierFilterOpen, setIsCourierFilterOpen] = useState(false);
    const [selectedCourier, setSelectedCourier] = useState(null);
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [andFilters, setAndFilters] = useState([]);
    const [courierList, setCourierList] = useState([]);
    const merchant = getMerchant();
    const merchantId = merchant.id ? merchant.id : '';

    const methods = useForm();
    const { data: orders = [], isFetching, isLoading, refetch } = useGetPtlPurQuery({
        limit: sizePerPage,
        offset: (page) * sizePerPage,
        orfilter: [],
        andfilter: [
            {
                field: "merchantDetail.key",
                operator: "is",
                value: merchantId
            },
            ...andFilters
        ]
    });

    const { data } = useGetAllCourierPartnersQuery({
        "andfilter": [{ "field": "is_deleted", "operator": "eq", "value": '0' }],
        offset: 0, limit: 500,
        "includes": "id,name,parent,prozo_id"
    });

    useEffect(() => {
        setPage(0);
    }, [sizePerPage, andFilters]);

    useEffect(() => {
        getGeneric(`${apiConstants.MERCHANT_DETAILS}/${merchantId}`)
            .then((response) => {
                if (response?.data?.b2bCheapestCPList) {
                    setCourierList(response.data.b2bCheapestCPList);
                } else {
                    setCourierList([]);
                }
            })
            .catch((err) => {
                console.error(err);
            });
    }, [merchantId]);

    let pickupStatus = pickupRequestStatus.map((op) => ({ label: _.startCase(op), value: op }));
    const filteredData = data && data.filter(item => courierList.includes(item.prozoId));
    let courierData = filteredData && filteredData.map((list) => ({ label: list.name, value: list.id }));

    const columns = [
        {
            field: 'reference',
            headerAlign: 'center',
            hideSortIcons: true,
            headerName: 'Reference',
            flex: 1,
            minWidth: 180,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Box sx={cellStackSx}>
                        <Typography sx={primaryTextSx}>{row.reference}</Typography>
                        {row.createdDate && (
                            <Typography sx={secondaryTextSx}>{parseDateAndTime(row.createdDate)}</Typography>
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'courier',
            headerAlign: 'center',
            hideSortIcons: true,
            headerName: 'Courier Partner',
            flex: 0.9,
            minWidth: 180,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Box sx={cellStackSx}>
                        {row.awb_number ? (
                            <Typography sx={awbTextSx}>
                                <Link style={{ color: '#1d4ed8', textDecoration: 'none', borderBottom: '1px dashed #93c5fd' }} to={`/tracking/${row.awb_number}`}>
                                    {row.awb_number}
                                </Link>
                            </Typography>
                        ) : (
                            <Typography sx={secondaryTextSx}>-</Typography>
                        )}
                        <Typography sx={secondaryTextSx}>{row?.courierDetail?.prozoId || '-'}</Typography>
                    </Box>
                );
            },
        },
        {
            field: 'orderReadyDate',
            headerAlign: 'center',
            hideSortIcons: true,
            headerName: 'Order Ready Date',
            flex: 0.8,
            minWidth: 150,
            renderCell: (params) => {
                const { row } = params;
                return <Typography sx={secondaryTextSx}>{parseDateAndTime(row?.orderReadyDate, row?.orderReadyTime)}</Typography>;
            },
        },
        {
            field: 'paymentDetails',
            headerAlign: 'center',
            hideSortIcons: true,
            headerName: 'Payment Mode',
            flex: 0.7,
            minWidth: 130,
            renderCell: (params) => {
                const { row } = params;
                const paymentMode = row.paymentType ? row.paymentType : '';
                const color = getPaymentColorForPickup(paymentMode);
                return (
                    <Box sx={cellStackSx}>
                        <Typography sx={secondaryTextSx}>{row.orderType}</Typography>
                        {paymentMode && (
                            <Chip
                                size="small"
                                label={paymentMode}
                                sx={{
                                    fontFamily: FONT,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    height: 24,
                                    bgcolor: color + '18',
                                    color: color,
                                    border: '1px solid ' + color + '40',
                                    '& .MuiChip-label': { px: 1 },
                                }}
                            />
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'warehouseName',
            headerAlign: 'center',
            hideSortIcons: true,
            headerName: 'Merchant WH Name',
            flex: 0.9,
            minWidth: 160,
            renderCell: (params) => {
                const { row } = params;
                return <Typography sx={secondaryTextSx}>{row.warehouseName}</Typography>;
            },
        },
        {
            field: 'delivery',
            headerAlign: 'center',
            hideSortIcons: true,
            headerName: 'Delivery Details',
            flex: 1,
            minWidth: 170,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Box sx={cellStackSx}>
                        <Typography sx={secondaryTextSx}>
                            {row.dropoffLocation.city}, {row.dropoffLocation.region}
                        </Typography>
                        <Typography sx={secondaryTextSx}>
                            {row.dropoffLocation.zip}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'boxes',
            headerAlign: 'center',
            hideSortIcons: true,
            headerName: 'Packages',
            flex: 0.8,
            minWidth: 120,
            renderCell: (params) => {
                const { row } = params;
                let d = row?.dimensions;
                let k = d?.reduce((sum, item) => sum + item.count, 0);
                return (
                    <Box sx={cellStackSx}>
                        <Typography sx={primaryTextSx}>{d.length}</Typography>
                        <Typography sx={secondaryTextSx}>Total Units: {k}</Typography>
                    </Box>
                );
            },
        },
        {
            field: 'weight',
            headerAlign: 'center',
            hideSortIcons: true,
            headerName: 'Weight (kg)',
            flex: 0.6,
            minWidth: 100,
            renderCell: (params) => {
                const { row } = params;
                if (row?.totalPackageWeight) {
                    return (
                        <Typography sx={secondaryTextSx}>
                            {(row?.totalPackageWeight / 1000).toFixed(2)} kg
                        </Typography>
                    );
                } else {
                    return <Typography sx={secondaryTextSx}>-</Typography>;
                }
            },
        },
        {
            field: 'allocation',
            headerAlign: 'center',
            hideSortIcons: true,
            headerName: 'Auto Allocation',
            flex: 0.5,
            minWidth: 110,
            renderCell: (params) => {
                const { row } = params;
                const isAuto = row.isAutoAllocateWhileUpload;
                return (
                    <Typography sx={{ ...secondaryTextSx, color: isAuto ? SUCCESS : ERROR, fontWeight: WEIGHT.semibold }}>
                        {isAuto ? 'Yes' : 'No'}
                    </Typography>
                );
            },
        },
        {
            field: 'status',
            headerAlign: 'center',
            hideSortIcons: true,
            headerName: 'Status',
            flex: 1.2,
            minWidth: 150,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Chip
                        size="small"
                        label={_.startCase(row?.status)}
                        sx={getPickupStatusChipSx(row?.status)}
                    />
                );
            },
        },
        {
            field: 'action2',
            headerAlign: 'center',
            hideSortIcons: true,
            headerName: 'Actions',
            flex: 0.6,
            minWidth: 100,
            renderCell: (params) => {
                const { row } = params;
                const isCancelDisabled = row.status === '1212';
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="View Details">
                            <IconButton
                                component={Link}
                                to={`/ptl-pickup-requests/${row.id}`}
                                color="primary"
                                size="small"
                            >
                                <Visibility fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        {row && !CLOSED_PICKUP_STATUSES.includes(row.status) && (
                            <Tooltip title="Cancel Pickup Request">
                                <IconButton
                                    aria-label="cancel"
                                    disabled={isCancelDisabled}
                                    onClick={() => { toggleCancel(row.reference, row.merchantDetail.key); }}
                                    size="small"
                                    sx={{
                                        backgroundColor: isCancelDisabled ? '#f1f1f1' : 'inherit',
                                    }}
                                >
                                    <Cancel fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                );
            },
        },
    ];

    const { handleSubmit, reset } = methods;
    const filterVal = useForm({
        defaultValues: {
            status: '',
            reference: '',
            fromDate: '',
            toDate: '',
            courier: ''
        }
    });
    const { handleSubmit: handleFilterSubmit, reset: filterReset } = filterVal;

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        let formData = new FormData();
        formData.append('file', data?.file[0]);
        try {
            await postMultipart(`${apiConstants.BULK_PTL_PUR}?merchantOid=${merchantId}`, formData).then((res) => {
                if (res.status === 200 && res.data?.success === true) {
                    setIsSubmitting(false);
                    enqueueSnackbar('Successfully uploaded!', { variant: 'success' });
                    refetch();
                    reset();
                } else if (res.status === 200 && res.data?.success === false) {
                    setIsSubmitting(false);
                    enqueueSnackbar(res.data?.messages, { variant: 'error' });
                    reset();
                } else {
                    enqueueSnackbar(`${res.data}`, { variant: 'error' });
                    setIsSubmitting(false);
                    reset();
                }
            });
            reset();
        } catch (err) {
            const message = err.response?.data?.message || err.message;
            enqueueSnackbar(message, { variant: 'error' });
            reset();
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setIsCourierFilterOpen(false);
    };

    const handleSave = () => {
        if (selectedCourier) {
            setBulkLoading(true);
            let payload = {
                references: selectedReferences,
                courierPrzId: selectedCourier
            };
            postGeneric(apiConstants.MANUAL_ALLOCATE_COURIER, payload).then(response => {
                setBulkLoading(false);
                if (response.status === 200 && response?.data?.success) {
                    enqueueSnackbar(response?.data?.message, { variant: 'success' });
                    refetch();
                    setSelectedCourier(null);
                    setSelectedReferences([]);
                    setRowSelectionModel([]);
                } else {
                    setBulkLoading(false);
                    enqueueSnackbar(response?.data?.message, { variant: 'success' });
                    refetch();
                    setSelectedCourier(null);
                    setSelectedReferences([]);
                    setRowSelectionModel([]);
                }
            }).catch((err) => {
                setBulkLoading(false);
                console.log(err);
                let message = err?.response?.data?.message ? err?.response?.data?.message : "Something went wrong while Allocating courier";
                enqueueSnackbar(message, { variant: "error" });
                setSelectedCourier(null);
                setSelectedReferences([]);
                setRowSelectionModel([]);
            });
        }
        setIsCourierFilterOpen(false);
    };

    const [cancelConfirm, setCancelConfirm] = useState(false);
    const [orderId, setOrderId] = useState(false);
    const [merId, setMerId] = useState(false);

    const toggleCancel = (orderId, merchantId) => {
        setCancelConfirm(true);
        setOrderId(orderId);
        setMerId(merchantId);
    };

    const handleCancelOrder = () => {
        let payload = {
            references: [orderId],
            merchantOid: merId
        };
        postGeneric(apiConstants.MANUAL_CANCEL_COURIER, payload).then(response => {
            if (response.status === 200) {
                refetch();
                enqueueSnackbar('Pickup request cancelled successfully', { variant: 'success' });
            }
        }).catch((err) => {
            console.log(err);
            let message = err?.response?.data?.message ? err?.response?.data?.message : "Something went wrong while cancelling pickup request";
            enqueueSnackbar(message, { variant: "error" });
        });
    };

    useEffect(() => {
        const references = orders
            .filter((val) => rowSelectionModel.includes(val.id))
            .map((order) => order.reference);
        setSelectedReferences(references);
    }, [rowSelectionModel, orders]);

    const applyFilters = async (filters) => {
        let appliedFilters = [];
        function formatDate(date) {
            return `Date('${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} 00:00:00')`;
        }
        if (filters.courier) {
            appliedFilters.push({ "field": "courierDetail.key", "operator": "eq", "value": filters.courier });
        }
        if (filters.reference) {
            appliedFilters.push({ "field": "reference", "operator": "eq", "value": filters.reference });
        }
        if (filters.status) {
            appliedFilters.push({ "field": "status", "operator": "eq", "value": filters.status });
        }
        if (filters.fromDate) {
            const fromDate = new Date(filters.fromDate);
            appliedFilters.push({ field: "orderReadyDate", operator: "gte", value: formatDate(fromDate) });
        }
        if (filters.toDate) {
            const toDate = new Date(filters.toDate);
            appliedFilters.push({ field: "orderReadyDate", operator: "lte", value: formatDate(toDate) });
        }
        setAndFilters(appliedFilters);
    };

    return (
        <Box sx={pageContainerSx}>
            <ConfirmDialog
                title=''
                open={cancelConfirm}
                setOpen={setCancelConfirm}
                onConfirm={() => handleCancelOrder()}
            >
                Are you sure you want to cancel this request?
            </ConfirmDialog>
            <PageTitle subTitle="Track and manage your scheduled pickup requests">PTL Pickup Requests</PageTitle>
            {(isFetching || isLoading) && <Loader />}

            <Box sx={filterBarSx}>
                <FormProvider methods={filterVal} onSubmit={handleFilterSubmit(applyFilters)}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={2}>
                            <RHFSelectField width="100%" name="courier" label="Select Courier" size="small" options={_.orderBy(courierData, 'courier')} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <RHFTextField name="reference" label="Reference Number" size="small" />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <RHFDateRangePickerField name="order_ready_date" label="Order Ready Date" size="small" maxDate={new Date()} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <RHFSelectField width="100%" name="status" label="Select Status" size="small" options={_.orderBy(pickupStatus, 'status')} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={btnRowSx}>
                                <Button type="submit" variant="contained" sx={primaryBtnSx}>Filter</Button>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    sx={secondaryBtnSx}
                                    onClick={() => {
                                        filterReset({
                                            status: "",
                                            courier: '',
                                            merchant: '',
                                            reference: '',
                                            fromDate: '',
                                            toDate: '',
                                        });
                                        setAndFilters([]);
                                    }}
                                >
                                    Reset
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </FormProvider>
            </Box>

            <Box sx={uploadSectionSx}>
                <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                    <MUIModal open={isCourierFilterOpen} handleClose={handleClose} title="Select Courier Partner">
                        {isCourierFilterOpen && (
                            <>
                                <WhitelistB2BCourier id={merchantId} setSelectedCourier={setSelectedCourier} />
                                {selectedCourier && (
                                    <Button variant="contained" sx={primaryBtnSx} onClick={handleSave}>
                                        Save
                                    </Button>
                                )}
                            </>
                        )}
                    </MUIModal>
                    <Box sx={{ mb: 2 }}>
                        <RHFFileField name='file' required={true} fileType={['xlsx']} />
                    </Box>
                    <Box sx={btnRowSx}>
                        <LoadingButton
                            loading={isSubmitting}
                            variant="contained"
                            size="small"
                            startIcon={<UploadIcon />}
                            sx={primaryBtnSx}
                            type="submit"
                        >
                            Upload
                        </LoadingButton>
                        <DownloadSampleFile fileName={"B2B_PICKUP_SAMPLE_NO_MER_ID"} excel={"excel"} label='SAMPLE' />
                        {selectedReferences.length > 0 && (
                            <LoadingButton
                                variant="contained"
                                size="small"
                                sx={primaryBtnSx}
                                onClick={() => setIsCourierFilterOpen(true)}
                                loading={bulkLoading}
                            >
                                Bulk Allocate Courier
                            </LoadingButton>
                        )}
                    </Box>
                </FormProvider>
            </Box>

            <ProzoDataGrid
                columns={columns}
                autoHeight={true}
                rows={(orders && orders.length > 0) ? orders : []}
                sizePerPage={sizePerPage}
                setSizePerPage={setSizePerPage}
                rowSelectionModel={rowSelectionModel}
                setRowSelectionModel={setRowSelectionModel}
                setPage={setPage}
                checkboxSelection={true}
                isRowSelectable={(params) => !(params.row.courierDetail || CLOSED_PICKUP_STATUSES.includes(params.row.status))}
                page={page}
                pagination={true}
                rowCount={Number.MAX_VALUE}
                hideCountNumber={true}
                getRowId={(row) => row.id}
            />
        </Box>
    );
}

export default withLayout(PtlPickupRequests);
